import React, { FC, ReactNode, useMemo } from 'react';

import { EvSimpleBarChart, renderCustomTooltip } from '@evinced-private/ui-common';

import { EvChartTableLink } from 'src/common/components/ev-chart-table-link/EvChartTableLink';
import {
	customSrSummaryRenderer,
	prepareDataForIssueTypesChart
} from 'src/common/helpers/MobileChartsHelper';
import { MobileIssueTypeCount } from 'src/common/types/MobileSingleSessionTypes';

import './MobileTopIssueTypesBarChart.scss';

interface IMobileTopIssueTypesBarChartProps {
	data: MobileIssueTypeCount[];
	onBarClick: (issueType: string) => void;
}

export const MobileTopIssueTypesBarChart: FC<IMobileTopIssueTypesBarChartProps> = ({
	data = [],
	onBarClick
}) => {
	const { barData, issueTypePatterns } = useMemo(() => {
		const barData = prepareDataForIssueTypesChart(data);

		const issueTypePatterns = barData.map(({ id, label, color }) => ({
			id: String(id),
			label: String(label),
			color: String(color)
		}));

		return { barData, issueTypePatterns };
	}, [data]);

	const issueTypeFieldFormatter = (cell: string): ReactNode => {
		return <EvChartTableLink value={cell} onClick={() => onBarClick(cell)} />;
	};

	return (
		<EvSimpleBarChart
			title="Top Issue Types"
			data={barData}
			patterns={issueTypePatterns}
			className="mobile-top-issue-types-bar-chart"
			width="100%"
			height={400}
			pointWidth={50}
			headerTooltipProps={{
				tooltipText: 'This chart shows the top 5 issue types by frequency.',
				tooltipTitle: 'Top Issue Types Information',
				tooltipPlacement: 'right'
			}}
			customSrSummaryRenderer={() => customSrSummaryRenderer(barData, 'Top Issue Types', 'issue')}
			customTooltip={(context: { y: number; name: string }): string => {
				const { y, name } = context;
				return renderCustomTooltip(y, name, 'Issue');
			}}
			onBarClick={(point) => onBarClick(String(point.name))}
			withLegend={false}
			isFocusable={true}
			isInteractive={true}
			enableFocusBorder={true}
			tableProps={{
				dataType: 'Issue Type',
				columnsPattern: [
					{
						name: 'Issue Type',
						field: 'label',
						formatter: issueTypeFieldFormatter
					},
					{
						name: 'Total Issues',
						field: 'count'
					},
					{
						name: 'Percentage',
						field: 'value',
						formatter: (value: number) => `${value}%`
					}
				]
			}}
		/>
	);
};
